import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Heart,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Image as ImageIcon,
  Video,
  Link2,
  Send,
  Loader2,
  Pin,
  Trash2,
  Edit,
  BarChart3,
  Clock,
  Globe,
  Users,
  Lock,
  Calendar,
  Hash,
  X,
  ChevronDown,
  Eye,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow, format, parseISO } from "date-fns";

// Post Card Component
export const PostCard = ({ 
  post, 
  onLike, 
  onComment, 
  onDelete, 
  onEdit,
  onPin,
  showAnalytics = false,
  currentUserId 
}) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const isAuthor = currentUserId === post.author_id;
  const { token } = useAuth();

  const loadComments = async () => {
    if (loadingComments) return;
    setLoadingComments(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/social/posts/${post.post_id}/comments`, { headers });
      setComments(res.data.comments || []);
    } catch (error) {
      console.error("Failed to load comments:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleToggleComments = () => {
    if (!showComments && comments.length === 0) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !token) return;
    setSubmittingComment(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/social/posts/${post.post_id}/comments`,
        { content: newComment.trim() },
        { headers }
      );
      setComments([...comments, res.data]);
      setNewComment("");
      onComment?.(post.post_id);
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/social/posts/${post.post_id}/analytics`, { headers });
      setAnalytics(res.data);
      setShowAnalyticsModal(true);
    } catch (error) {
      toast.error("Failed to load analytics");
    }
  };

  const getVisibilityIcon = () => {
    switch (post.visibility) {
      case "public": return <Globe className="w-3 h-3" />;
      case "followers": return <Users className="w-3 h-3" />;
      case "team_only": return <Lock className="w-3 h-3" />;
      default: return <Globe className="w-3 h-3" />;
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-2xl overflow-hidden ${post.is_pinned ? 'ring-2 ring-amber-400' : ''}`}>
      {/* Pinned Badge */}
      {post.is_pinned && (
        <div className="bg-amber-50 px-4 py-2 flex items-center gap-2 text-amber-700 text-sm">
          <Pin className="w-4 h-4" />
          Pinned Post
        </div>
      )}

      {/* Header */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={post.author?.avatar_url} />
            <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
              {post.author?.name?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{post.author?.name}</span>
              {post.org && (
                <span className="text-gray-500">•</span>
              )}
              {post.org && (
                <span className="text-indigo-600 text-sm font-medium">{post.org.name}</span>
              )}
              {post.team && (
                <Badge variant="outline" className="text-xs">
                  {post.team.name}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{formatDistanceToNow(parseISO(post.published_at || post.created_at), { addSuffix: true })}</span>
              <span>•</span>
              {getVisibilityIcon()}
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {showAnalytics && (
              <DropdownMenuItem onClick={loadAnalytics}>
                <BarChart3 className="w-4 h-4 mr-2" />
                View Analytics
              </DropdownMenuItem>
            )}
            {isAuthor && (
              <>
                <DropdownMenuItem onClick={() => onEdit?.(post)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Post
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPin?.(post)}>
                  <Pin className="w-4 h-4 mr-2" />
                  {post.is_pinned ? "Unpin" : "Pin"} Post
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete?.(post.post_id)} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Post
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-gray-800 whitespace-pre-wrap">{post.content}</p>
        
        {/* Hashtags */}
        {post.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {post.hashtags.map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs cursor-pointer hover:bg-indigo-100">
                #{tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Media */}
      {post.media_urls?.length > 0 && (
        <div className={`grid gap-1 ${post.media_urls.length === 1 ? '' : 'grid-cols-2'}`}>
          {post.media_urls.slice(0, 4).map((url, idx) => (
            <img
              key={idx}
              src={url}
              alt=""
              className="w-full h-64 object-cover"
            />
          ))}
        </div>
      )}

      {/* Link Preview */}
      {post.link_url && post.link_preview && (
        <a 
          href={post.link_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="mx-4 mb-3 block border border-gray-200 rounded-xl overflow-hidden hover:bg-gray-50 transition-colors"
        >
          {post.link_preview.image && (
            <img src={post.link_preview.image} alt="" className="w-full h-40 object-cover" />
          )}
          <div className="p-3">
            <p className="font-medium text-gray-900 line-clamp-1">{post.link_preview.title}</p>
            <p className="text-sm text-gray-500 line-clamp-2">{post.link_preview.description}</p>
            <p className="text-xs text-gray-400 mt-1">{new URL(post.link_url).hostname}</p>
          </div>
        </a>
      )}

      {/* Stats Bar */}
      <div className="px-4 py-2 flex items-center justify-between text-sm text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-4">
          {post.like_count > 0 && (
            <span>{post.like_count} likes</span>
          )}
          {post.comment_count > 0 && (
            <span>{post.comment_count} comments</span>
          )}
        </div>
        {post.view_count > 0 && (
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {post.view_count} views
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-2 flex items-center gap-1 border-t border-gray-100">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onLike?.(post.post_id, !post.user_liked)}
          className={`flex-1 gap-2 ${post.user_liked ? 'text-rose-600' : ''}`}
        >
          <Heart className={`w-4 h-4 ${post.user_liked ? 'fill-current' : ''}`} />
          {post.user_liked ? "Liked" : "Like"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleComments}
          className="flex-1 gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          Comment
        </Button>
        <Button variant="ghost" size="sm" className="flex-1 gap-2">
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {/* Comment Input */}
          {token && (
            <div className="flex items-start gap-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">U</AvatarFallback>
              </Avatar>
              <div className="flex-1 flex items-center gap-2">
                <Input
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitComment()}
                  className="flex-1"
                />
                <Button 
                  size="sm" 
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || submittingComment}
                >
                  {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}

          {/* Comments List */}
          {loadingComments ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {comments.map((comment) => (
                <div key={comment.comment_id} className="flex items-start gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={comment.author?.avatar_url} />
                    <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
                      {comment.author?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">{comment.author?.name}</span>
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(parseISO(comment.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.content}</p>
                  </div>
                </div>
              ))}
              {comments.length === 0 && !loadingComments && (
                <p className="text-center text-gray-500 text-sm py-2">No comments yet. Be the first!</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Analytics Modal */}
      <Dialog open={showAnalyticsModal} onOpenChange={setShowAnalyticsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Post Analytics
            </DialogTitle>
          </DialogHeader>
          {analytics && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <Eye className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="text-2xl font-bold text-gray-900">{analytics.view_count}</p>
                <p className="text-xs text-gray-500">Views</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-4 text-center">
                <Heart className="w-6 h-6 mx-auto mb-2 text-rose-400" />
                <p className="text-2xl font-bold text-rose-600">{analytics.like_count}</p>
                <p className="text-xs text-gray-500">Likes</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <MessageCircle className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                <p className="text-2xl font-bold text-blue-600">{analytics.comment_count}</p>
                <p className="text-xs text-gray-500">Comments</p>
              </div>
            </div>
          )}
          {analytics && (
            <div className="mt-4 p-4 bg-green-50 rounded-xl">
              <p className="text-sm text-green-700">
                <strong>{analytics.engagement_rate}%</strong> engagement rate
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Create Post Component
export const CreatePostForm = ({ 
  orgId, 
  teamId, 
  onPostCreated,
  postType = "org",
  className = ""
}) => {
  const { token, user } = useAuth();
  const [content, setContent] = useState("");
  const [mediaUrls, setMediaUrls] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [visibility, setVisibility] = useState("followers"); // Default to followers only
  const [scheduledFor, setScheduledFor] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error("Please write something");
      return;
    }

    setPosting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        content: content.trim(),
        post_type: postType,
        org_id: orgId,
        team_id: teamId,
        visibility,
        media_urls: mediaUrls,
        link_url: linkUrl || null,
        is_pinned: isPinned,
        scheduled_for: scheduledFor || null
      };

      const res = await axios.post(`${API}/social/posts`, payload, { headers });
      
      toast.success(scheduledFor ? "Post scheduled!" : "Post published!");
      setContent("");
      setMediaUrls([]);
      setLinkUrl("");
      setScheduledFor("");
      setIsPinned(false);
      onPostCreated?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    // TODO: Implement actual image upload to R2/S3
    toast.info("Image upload coming soon!");
  };

  if (!token) return null;

  return (
    <div className={`bg-white border border-gray-200 rounded-2xl p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Avatar className="w-10 h-10">
          <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
            {user?.first_name?.charAt(0) || "U"}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <Textarea
            placeholder="What's happening?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[80px] resize-none border-0 focus-visible:ring-0 p-0 text-base"
          />

          {/* Media Preview */}
          {mediaUrls.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {mediaUrls.map((url, idx) => (
                <div key={idx} className="relative">
                  <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                    onClick={() => setMediaUrls(mediaUrls.filter((_, i) => i !== idx))}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Link Input */}
          {linkUrl && (
            <div className="mt-2 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Paste a link..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="flex-1"
              />
              <Button variant="ghost" size="sm" onClick={() => setLinkUrl("")}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Schedule Input */}
          {showSchedule && (
            <div className="mt-2 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <Input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="flex-1"
              />
              <Button variant="ghost" size="sm" onClick={() => { setShowSchedule(false); setScheduledFor(""); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Actions Bar */}
      <div className="mt-3 flex flex-col gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            multiple
            className="hidden"
          />
          <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
            <ImageIcon className="w-4 h-4 text-green-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setLinkUrl(linkUrl || " ")}>
            <Link2 className="w-4 h-4 text-blue-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowSchedule(!showSchedule)}>
            <Clock className="w-4 h-4 text-orange-600" />
          </Button>
          {postType === "org" && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsPinned(!isPinned)}
              className={isPinned ? "text-amber-600" : ""}
            >
              <Pin className={`w-4 h-4 ${isPinned ? "fill-current" : ""}`} />
            </Button>
          )}
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          {/* Visibility Selector for Org Posts */}
          {postType === "org" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  {visibility === "public" ? <Globe className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                  {visibility === "public" ? "Public" : "Followers"}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setVisibility("followers")}>
                  <Users className="w-4 h-4 mr-2" />
                  Followers Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setVisibility("public")}>
                  <Globe className="w-4 h-4 mr-2" />
                  Public (Anyone)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Visibility Selector for Team Posts */}
          {postType === "team" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  {visibility === "team_only" ? <Lock className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                  {visibility === "team_only" ? "Team Only" : "Team + Followers"}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setVisibility("team_only")}>
                  <Lock className="w-4 h-4 mr-2" />
                  Team Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setVisibility("team_and_followers")}>
                  <Users className="w-4 h-4 mr-2" />
                  Team + Org Followers
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button 
            onClick={handleSubmit} 
            disabled={!content.trim() || posting}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {posting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : scheduledFor ? (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Schedule
              </>
            ) : (
              "Post"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Follow Button Component
export const FollowButton = ({ orgId, initialFollowing = false, onFollowChange }) => {
  const { token } = useAuth();
  const [following, setFollowing] = useState(initialFollowing);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    if (!token) {
      toast.error("Please log in to follow");
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (following) {
        await axios.delete(`${API}/social/orgs/${orgId}/follow`, { headers });
        setFollowing(false);
        toast.success("Unfollowed");
      } else {
        await axios.post(`${API}/social/orgs/${orgId}/follow`, {}, { headers });
        setFollowing(true);
        toast.success("Now following!");
      }
      onFollowChange?.(!following);
    } catch (error) {
      toast.error("Failed to update follow status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={following ? "outline" : "default"}
      onClick={handleToggle}
      disabled={loading}
      className={following ? "" : "bg-indigo-600 hover:bg-indigo-700"}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : following ? (
        "Following"
      ) : (
        "Follow"
      )}
    </Button>
  );
};

export default { PostCard, CreatePostForm, FollowButton };
